/** The Day ribbon (plan §5 "Days card", the itinerary's signature element): the trip's shape
 *  in one 28 px row — a segment per row, its width set by its span (min 6 px, 1 px gaps),
 *  white with a 1 px navy-35 % outline and its tabular day label where it fits ("3–4"),
 *  labels thinned like the schedule axis's ticks. The open day is navy-filled with a white
 *  label. Edit marks are a shape and words: a changed day carries an 8 px navy dot on its
 *  top edge, an added day a 12 % amber fill, a removed day a dashed disabled-grey outline
 *  until the save; the tooltip and accessible name say it ("Day 3–4 · Ayutthaya · 2
 *  changes"). It fits its card at every width and never scrolls (Axis-Never-Scrolls): the
 *  widths are fitted to the measured row, and before the first measurement the spans are
 *  the proportions.
 *
 *  Keyboard (the schedule axis's pattern): the segments are one tab stop — the open day's,
 *  else the first; ←/→ move between days, Home/End to the first/last; Enter (or a click)
 *  opens the day's row (`onOpen`). The outline below is its text equivalent
 *  (`describedBy`). */
import { alpha, Box, ButtonBase, Tooltip, Typography, type Theme } from '@mui/material';
import { useMemo, useRef, useState, type KeyboardEvent } from 'react';

import { useElementWidth } from '@/features/records/page/recordPage';

import {
  RIBBON_HEIGHT_PX,
  ribbonCounts,
  ribbonLayout,
  type DayMarks,
  type RibbonDay,
  type RibbonSegment,
} from './dayRibbonModel';

/** Room above the row for a changed day's dot, reserved in both modes (Zero-Re-Layout). */
export const DOT_ROOM_PX = 4;
const DOT_PX = 8;

const FOCUS_RING = {
  outline: '2px solid',
  outlineColor: 'primary.main',
  outlineOffset: 2,
} as const;

function segmentSx(s: RibbonSegment, open: boolean) {
  return (t: Theme) => {
    const navy = t.palette.primary.main;
    const state = s.mark?.state;
    const look =
      state === 'removed'
        ? { bgcolor: 'transparent', border: `1px dashed ${t.palette.text.disabled}` }
        : open
          ? { bgcolor: navy, border: `1px solid ${navy}` }
          : {
              bgcolor:
                state === 'added'
                  ? alpha(t.palette.warning.main, 0.12)
                  : t.palette.background.paper,
              border: `1px solid ${alpha(navy, 0.35)}`,
            };
    return {
      flex: `${s.grow} 1 0px`,
      minWidth: 0,
      height: RIBBON_HEIGHT_PX,
      position: 'relative',
      boxSizing: 'border-box',
      borderRadius: '3px',
      ...look,
      '&:hover': { zIndex: 1, boxShadow: `0 0 0 1px ${navy}` },
      '&.Mui-focusVisible': { zIndex: 2, ...FOCUS_RING },
    } as const;
  };
}

export interface DayRibbonProps {
  /** The rows, in order (removed ones included while the draft holds them). */
  days: readonly RibbonDay[];
  /** The day whose row is open below: navy-filled, and the ribbon's tab stop. */
  openDayId?: number | null;
  /** Enter or a click on a day: open its row. */
  onOpen: (dayId: number) => void;
  /** Edit mode: the draft's changed / added / removed days, by id (new days have negative
   *  ids). */
  marks?: DayMarks;
  /** The outline below — the ribbon's text equivalent. */
  describedBy?: string;
}

export function DayRibbon({ days, openDayId = null, onOpen, marks, describedBy }: DayRibbonProps) {
  const [rootRef, width] = useElementWidth<HTMLDivElement>();
  const { segments, gap } = useMemo(() => ribbonLayout(days, width, marks), [days, width, marks]);

  // Roving focus over the days, in order; the tab stop rests on the open day by default.
  const [activeId, setActiveId] = useState<number | null>(null);
  const refs = useRef<(HTMLElement | null)[]>([]);
  const indexOf = (id: number | null) =>
    id === null ? -1 : segments.findIndex((s) => s.id === id);
  const current = Math.max(0, indexOf(activeId) >= 0 ? indexOf(activeId) : indexOf(openDayId));
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const last = segments.length - 1;
    const next =
      e.key === 'ArrowRight'
        ? Math.min(last, current + 1)
        : e.key === 'ArrowLeft'
          ? Math.max(0, current - 1)
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? last
              : null;
    if (next === null || last < 0) return;
    e.preventDefault();
    setActiveId(segments[next]!.id);
    refs.current[next]?.focus();
  };

  return (
    // Measured on the root, which is there whether or not the itinerary has days yet.
    <Box ref={rootRef} sx={{ pt: `${DOT_ROOM_PX}px`, minWidth: 0 }} data-day-ribbon>
      {segments.length === 0 ? (
        <Box
          sx={{
            height: RIBBON_HEIGHT_PX,
            boxSizing: 'border-box',
            border: 1,
            borderStyle: 'dashed',
            borderColor: 'divider',
            borderRadius: '3px',
            display: 'flex',
            alignItems: 'center',
            px: 1,
          }}
          data-day-ribbon-empty
        >
          <Typography variant="caption" color="text.secondary">
            No days yet
          </Typography>
        </Box>
      ) : (
        <Box
          role="group"
          aria-label={`Day ribbon: ${ribbonCounts(days, marks)} — ← and → move between days, Enter opens the day`}
          {...(describedBy ? { 'aria-describedby': describedBy } : {})}
          onKeyDown={onKeyDown}
          sx={{ display: 'flex', gap: `${gap}px`, height: RIBBON_HEIGHT_PX, minWidth: 0 }}
        >
          {segments.map((s, i) => (
            <Segment
              key={s.id}
              segment={s}
              open={s.id === openDayId}
              tabIndex={i === current ? 0 : -1}
              register={(el) => {
                refs.current[i] = el;
              }}
              onFocus={() => setActiveId(s.id)}
              onOpen={() => onOpen(s.id)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}

function Segment({
  segment: s,
  open,
  tabIndex,
  register,
  onFocus,
  onOpen,
}: {
  segment: RibbonSegment;
  open: boolean;
  tabIndex: number;
  register: (el: HTMLElement | null) => void;
  onFocus: () => void;
  onOpen: () => void;
}) {
  return (
    <Tooltip title={s.name} placement="top">
      <ButtonBase
        ref={register}
        tabIndex={tabIndex}
        onFocus={onFocus}
        onClick={onOpen}
        aria-label={s.name}
        {...(open ? { 'aria-current': true } : {})}
        data-day-segment={s.id}
        data-day-mark={s.mark?.state}
        data-open={open || undefined}
        sx={segmentSx(s, open)}
      >
        {s.label && (
          <Typography
            component="span"
            variant="caption"
            aria-hidden
            sx={{
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              whiteSpace: 'nowrap',
              color: open ? 'primary.contrastText' : 'text.secondary',
              fontWeight: open ? 600 : 400,
            }}
            data-day-label
          >
            {s.label}
          </Typography>
        )}
        {s.mark?.state === 'changed' && (
          <Box
            component="span"
            aria-hidden
            data-day-dot
            sx={(t) => ({
              position: 'absolute',
              top: -DOT_PX / 2,
              left: '50%',
              transform: 'translateX(-50%)',
              width: DOT_PX,
              height: DOT_PX,
              borderRadius: '50%',
              bgcolor: open ? t.palette.background.paper : t.palette.primary.main,
              boxShadow: `0 0 0 1.5px ${open ? t.palette.primary.main : t.palette.background.paper}`,
            })}
          />
        )}
      </ButtonBase>
    </Tooltip>
  );
}
